import {
  Box,
  Chip,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import styled from "styled-components";

interface ProgressBarProps {
  progress: number | null;
  emptyColor?: string;
  filledColor?: string;
  fontColor?: string;
  progressPlacement?: "center" | "right" | "top" | "inside"; // Added "inside" option
  withShadow?: boolean;
  labels?: [string, string];
  explanations?: [string, string];
  height?: number;
  thresholdRange?: [number, number] | [];
}

const CustomTooltip = styled(({ className, children, title, ...props }: { className?: string; children?: React.ReactNode; title: string; [key: string]: any }) => (
  <Tooltip title={title} classes={{ popper: className }} {...props}>
    {children as React.ReactElement}
  </Tooltip>
))(({ theme }) => ({
  [`& .MuiTooltip-tooltip`]: {
    color: "#000",
    fontFamily: "Poppins",
    fontStyle: "normal",
    lineHeight: "166%",
    letterSpacing: "0.4px",
    padding: "8px",
    backgroundColor: "#F5F5F5",
    border: "1px solid #ccc",
    borderRadius: "5px",
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
  },
}));

const ErrorBar: React.FC<{
  from: number;
  to: number;
  height: number;
}> = ({ from, to, height }) => {
  return (
    <Box width="100%" position="absolute" top={0} left={0}>
      <svg width={"100%"} height={height} xmlns="http://www.w3.org/2000/svg">
        <line
          x1={`${from}%`}
          y1="50%"
          x2={`${to}%`}
          y2="50%"
          stroke="white"
          strokeWidth="2"
        />
        <line
          x1={`${from === 0 ? 1 : from}%`}
          y1={height * 0.3}
          y2={height * 0.7}
          x2={`${from === 0 ? 1 : from}%`}
          stroke="white"
          strokeWidth="2"
        />
        <line
          x1={`${to === 100 ? 99 : to}%`}
          x2={`${to === 100 ? 99 : to}%`}
          y1={height * 0.3}
          y2={height * 0.7}
          stroke="white"
          strokeWidth="2"
        />
      </svg>
    </Box>
  );
};

const ProgressBar: React.FC<ProgressBarProps> = ({
  progress,
  emptyColor = "#D7D7D7",
  filledColor = "purple",
  fontColor = "textSecondary",
  progressPlacement = "right",
  withShadow = false,
  height,
  labels = [],
  explanations = [],
  thresholdRange = [],
}) => {
  // Use a taller height for inside text placement
  const h =
    height ||
    (progressPlacement === "inside"
      ? 20
      : progressPlacement === "center"
      ? 12
      : 8);

  return (
    <Stack width="100%" spacing={1}>
      {progress === null && (
        <Typography
          variant="body2"
          color={fontColor}
          sx={{
            textAlign: "center",
            width: "100%",
          }}
        >
          N/A
        </Typography>
      )}
      {progress !== null && progressPlacement === "top" && (
        <Stack
          direction="row"
          sx={{ width: "100%", paddingLeft: 0 }}
          justifyContent="space-between"
        >
          <CustomTooltip title={explanations?.[0] || ""}>
            <Chip
              label={`${progress}%`}
              sx={{
                backgroundColor: "rgba(124, 77, 255, 0.12)",
                color: "#4E6355",
                borderRadius: 50,
              }}
            />
          </CustomTooltip>
          <CustomTooltip title={explanations?.[1] || ""}>
            <Chip
              label={`${100 - progress}%`}
              sx={{
                backgroundColor: "rgba(124, 77, 255, 0.12)",
                color: "#4E6355",
                borderRadius: 50,
              }}
            />
          </CustomTooltip>
        </Stack>
      )}

      {/* Progress bar with optional labels */}

      {/* Progress bar, error bar, and inside text */}
      <Box position="relative" width="100%" sx={{ pl: 0 }}>
        {progress === null ? (
          <Typography
            variant="body2"
            color={fontColor}
            sx={{
              textAlign: "center",
              width: "100%",
            }}
          >
            N/A
          </Typography>
        ) : (
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              minWidth: 100,
              width: "100%",
              height: h,
              borderRadius: 50,
              boxShadow: withShadow
                ? "inset 0px 2px 4px rgba(0, 0, 0, 0.15)"
                : "none",
              "& .MuiLinearProgress-bar": {
                backgroundColor: filledColor,
                boxShadow: "none",
                transition: "transform .4s linear",
              },
              backgroundColor: emptyColor,
            }}
          />
        )}
        {thresholdRange.length === 2 && (
          <ErrorBar
            height={h}
            from={thresholdRange[0]}
            to={thresholdRange[1]}
          />
        )}

        {/* Display percentage inside the progress bar */}
        {progress !== null && progressPlacement === "inside" && (
          <Typography
            variant="body2"
            fontWeight="light"
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: h,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#FFFFFF",
              fontSize: "10",
            }}
          >
            {`${Math.round(progress)}%`}
          </Typography>
        )}
      </Box>

      {/* Labels beneath the bar */}
      {labels.length === 2 && (
        <Stack
          direction="row"
          sx={{ width: "100%", paddingLeft: 0 }}
          justifyContent="space-between"
        >
          <Typography variant="caption" color={fontColor}>
            {labels[0]}
          </Typography>
          <Typography variant="caption" color={fontColor}>
            {labels[1]}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
};

export default ProgressBar;
